export type RuntimeActivityCategory = "startup" | "lazy" | "service-worker" | "sync" | "other";
export type RuntimeActivityStatus = "queued" | "running" | "done" | "error";

export interface RuntimeActivityEntry {
  id: string;
  label: string;
  category: RuntimeActivityCategory;
  status: RuntimeActivityStatus;
  detail: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
}

export interface RuntimeActivitySnapshot {
  entries: RuntimeActivityEntry[];
  activeCount: number;
  queuedCount: number;
  errorCount: number;
  updatedAt: string;
}

const MAX_ENTRIES = 80;
const listeners = new Set<() => void>();
let entries: RuntimeActivityEntry[] = [];
let snapshot: RuntimeActivitySnapshot = buildSnapshot();

function nowIso(): string { return new Date().toISOString(); }

function buildSnapshot(): RuntimeActivitySnapshot {
  return {
    entries: [...entries],
    activeCount: entries.filter((entry) => entry.status === "running").length,
    queuedCount: entries.filter((entry) => entry.status === "queued").length,
    errorCount: entries.filter((entry) => entry.status === "error").length,
    updatedAt: nowIso(),
  };
}

function emit(): void {
  snapshot = buildSnapshot();
  for (const listener of listeners) listener();
}

function upsert(entry: RuntimeActivityEntry): void {
  const index = entries.findIndex((item) => item.id === entry.id);
  if (index >= 0) entries = [entry, ...entries.filter((_, itemIndex) => itemIndex !== index)];
  else entries = [entry, ...entries];
  if (entries.length > MAX_ENTRIES) entries = entries.slice(0, MAX_ENTRIES);
  emit();
}

export function getRuntimeActivitySnapshot(): RuntimeActivitySnapshot { return snapshot; }
export function subscribeRuntimeActivity(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function queueRuntimeActivity(id: string, label: string, category: RuntimeActivityCategory, detail: string | null = null): void {
  const existing = entries.find((entry) => entry.id === id);
  upsert({
    id,
    label,
    category,
    status: "queued",
    detail,
    createdAt: existing?.createdAt ?? nowIso(),
    startedAt: null,
    finishedAt: null,
    durationMs: null,
  });
}

export function startRuntimeActivity(id: string, label: string, category: RuntimeActivityCategory, detail: string | null = null): void {
  const existing = entries.find((entry) => entry.id === id);
  upsert({
    id,
    label,
    category,
    status: "running",
    detail,
    createdAt: existing?.createdAt ?? nowIso(),
    startedAt: nowIso(),
    finishedAt: null,
    durationMs: null,
  });
}

export function finishRuntimeActivity(id: string, detail: string | null = null): void {
  const existing = entries.find((entry) => entry.id === id);
  if (!existing) return;
  const finishedAt = nowIso();
  const durationMs = existing.startedAt ? Math.max(0, Date.parse(finishedAt) - Date.parse(existing.startedAt)) : null;
  upsert({ ...existing, status: "done", detail: detail ?? existing.detail, finishedAt, durationMs });
}

export function failRuntimeActivity(id: string, error: unknown, detail: string | null = null): void {
  const existing = entries.find((entry) => entry.id === id);
  const finishedAt = nowIso();
  const errorText = error instanceof Error ? error.message : String(error);
  const base = existing ?? {
    id,
    label: id,
    category: "other" as const,
    status: "running" as const,
    detail: null,
    createdAt: finishedAt,
    startedAt: finishedAt,
    finishedAt: null,
    durationMs: null,
  };
  const durationMs = base.startedAt ? Math.max(0, Date.parse(finishedAt) - Date.parse(base.startedAt)) : null;
  upsert({ ...base, status: "error", detail: detail ? `${detail} · ${errorText}` : errorText, finishedAt, durationMs });
}

export async function trackRuntimePromise<T>(
  id: string,
  label: string,
  category: RuntimeActivityCategory,
  task: () => Promise<T>,
  detail: string | null = null,
): Promise<T> {
  startRuntimeActivity(id, label, category, detail);
  try {
    const result = await task();
    finishRuntimeActivity(id);
    return result;
  } catch (error) {
    failRuntimeActivity(id, error);
    throw error;
  }
}

export function clearFinishedRuntimeActivities(): void {
  entries = entries.filter((entry) => entry.status === "queued" || entry.status === "running");
  emit();
}
